import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CodeIcon from '@mui/icons-material/Code';
import { useUser } from '../../context/UserContext';

const Navbar = () => {
  const { user, logout } = useUser();
  const navigate = useNavigate();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [codeIconClickCount, setCodeIconClickCount] = useState(0);

  const menuRef = useRef(null);
  const menuButtonRef = useRef(null); // Renamed from iconRef for clarity

  // Toggle menu state
  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  // Handle user logout
  const handleLogout = useCallback(() => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
  }, [logout, navigate]);

  // Close menu on outside click
  const handleClickOutside = useCallback((event) => {
    if (
      menuRef.current && !menuRef.current.contains(event.target) &&
      menuButtonRef.current && !menuButtonRef.current.contains(event.target)
    ) {
      setIsMenuOpen(false);
    }
  }, []);

  // Close menu on Escape key press
  const handleEsc = useCallback((event) => {
    if (event.key === 'Escape') {
      setIsMenuOpen(false);
    }
  }, []);

  // Effect for handling menu open/close side effects (event listeners)
  useEffect(() => {
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEsc);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    }

    // Cleanup function to remove event listeners when component unmounts or menu closes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isMenuOpen, handleClickOutside, handleEsc]);

  // Effect for handling the secret login navigation
  useEffect(() => {
    let timeoutId;
    if (codeIconClickCount === 7) {
      navigate('/login');
      setCodeIconClickCount(0); // Reset count after navigation
    } else if (codeIconClickCount > 0) {
      // Set a timeout to reset the count if no further clicks occur within 3 seconds
      timeoutId = setTimeout(() => {
        setCodeIconClickCount(0);
      }, 3000);
    }

    // Clear the timeout if the component unmounts or the count changes
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [codeIconClickCount, navigate]); // Depend on codeIconClickCount and navigate

  // Handle clicks on the CodeIcon for the secret feature
  const handleCodeIconClick = useCallback(() => {
    setCodeIconClickCount(prev => prev + 1);
  }, []);

  return (
    <div className='navBar-wrapper'>
      <nav className="navbar">
        <div className="logo">
          <CodeIcon
            onClick={handleCodeIconClick}
            aria-label="Code icon for secret navigation" // Added for accessibility
          />
          <NavLink to="/#portfolio-home" className="logo-link">
            NavarroJalen
          </NavLink>
        </div>
        <MenuIcon
          ref={menuButtonRef}
          className="menu-icon"
          onClick={toggleMenu}
          aria-expanded={isMenuOpen} // Added for accessibility
          aria-controls="side-menu-navigation" // Added for accessibility
        />
      </nav>



      <div
        ref={menuRef}
        id="side-menu-navigation" // Added ID for aria-controls
        className={`side-menu ${isMenuOpen ? 'open' : ''}`}
        role="navigation" // Added for accessibility
        aria-label="Main navigation" // Added for accessibility
      >
       
        <NavLink to="/" onClick={() => setIsMenuOpen(false)}>Portfolio</NavLink>
        {/* <NavLink to="/webstore" onClick={() => setIsMenuOpen(false)}>Webstore</NavLink> */}

        <hr className="divider" />
        <NavLink to="https://navarrojalen.netlify.app/videos/public/navarrojalenshowcase" onClick={() => setIsMenuOpen(false)}>Showcase</NavLink>        
        <NavLink to="/resume" onClick={() => setIsMenuOpen(false)}>Resume</NavLink>

        <hr className="divider" />
        {user ? (
          <>
            <NavLink to="/dashboard" onClick={() => setIsMenuOpen(false)}>Dashboard</NavLink>
            <button onClick={handleLogout}>Logout</button>
          </>
        ) : (
          // Consider adding login/signup links here if not logged in
          null
        )}
      </div>
    </div>
  );
};

export default Navbar;